import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import toast from 'react-hot-toast'
import axiosInstant from '../../api'
import {
  getProductCategories,
  rmnProductsEndpoint,
  rmnPromoteDiscoveredBrandEndpoint,
  rmnPromoteDiscoveredProductEndpoint,
  searchBrand,
} from '../../api/endpoints'
import { apiErrorCode } from '../../utils/apiError'
import { useNetworkScope } from '../../hooks/useNetworkScope'
import { RmnDiscoveredBrand, RmnDiscoveredProduct } from '../../types/rmn'
import { CategoryOption, groupedCategoryOptions } from './discoveredImportShared'

interface MatchOption {
  id: number
  label: string
  image?: string | null
}

type ImportDiscoveredModalProps = {
  onClose: () => void
  onImported: () => void
} & (
  | { mode: 'product'; record: RmnDiscoveredProduct }
  | { mode: 'brand'; record: RmnDiscoveredBrand }
)

// Client-facing import review (adapted from the internal PromoteMatchModal):
// the crawled record on the left with a REQUIRED TDC category pick for
// products (pre-filled from the crawl's identified category), network
// catalog search on the right. Picking a result links the crawled row to
// it; "Import as new" creates a fresh catalog record. The server always
// associates the account brand (+ the supplier brand for RMN networks —
// shown in the "Will be linked to" line; never sent from here).
export function ImportDiscoveredModal(props: ImportDiscoveredModalProps) {
  const { onClose, onImported } = props
  const { isNetwork: isRmn } = useNetworkScope()
  const isProduct = props.mode === 'product'
  const noun = isProduct ? 'product' : 'brand'
  const stagedName = isProduct
    ? props.record.title || props.record.url
    : props.record.name
  const stagedImage = isProduct ? props.record.image : props.record.logo
  const stagedUrl = props.record.url

  const supplierLabel =
    props.mode === 'product' && isRmn
      ? (props.record.matched_brand && props.record.matched_brand.name) ||
        props.record.brand_text ||
        null
      : null

  // Pre-select the record's existing match (auto-match / crawl passes) so
  // the primary CTA reads "Link to …" — the row will connect to the
  // existing record, never duplicate it.
  const seededMatch: MatchOption | null =
    props.mode === 'product'
      ? props.record.matched_desire
        ? {
            id: props.record.matched_desire.desireid,
            label: props.record.matched_desire.title,
          }
        : null
      : props.record.matched_brand
        ? {
            id: props.record.matched_brand.id,
            label: props.record.matched_brand.name,
            image: props.record.matched_brand.logo,
          }
        : null

  const [query, setQuery] = useState(stagedName || '')
  const [options, setOptions] = useState<MatchOption[]>([])
  const [selected, setSelected] = useState<MatchOption | null>(seededMatch)
  const [searching, setSearching] = useState(false)
  const [importing, setImporting] = useState(false)
  const [inlineError, setInlineError] = useState<string | null>(null)

  const [categories, setCategories] = useState<CategoryOption[]>([])
  const [categoryId, setCategoryId] = useState<number | ''>(
    props.mode === 'product' && props.record.product_category_id
      ? props.record.product_category_id
      : ''
  )

  // The category picker options (products only).
  useEffect(() => {
    if (!isProduct) return undefined
    let stale = false
    axiosInstant
      .get(getProductCategories())
      .then((res) => {
        if (!stale) setCategories((res.data || []) as CategoryOption[])
      })
      .catch(() => {
        if (!stale) setCategories([])
      })
    return () => {
      stale = true
    }
  }, [isProduct])

  // Search-as-you-type against the network catalog (debounced). Products go
  // through the network-scoped /rmn/products (rows keyed `desireid`); brands
  // through the open /brands search.
  useEffect(() => {
    let stale = false
    const q = query.trim()
    if (!q) {
      setOptions([])
      return undefined
    }
    const timer = window.setTimeout(async () => {
      setSearching(true)
      try {
        let rows: any[] = []
        if (isProduct) {
          const res = await axiosInstant.get(rmnProductsEndpoint, {
            params: { search: q, limit: 10 },
          })
          rows = (res.data && res.data.rows) || []
          if (!stale) {
            setOptions(
              rows.slice(0, 10).map((row: any) => ({
                id: row.desireid,
                label: row.title,
                image: row.image,
              }))
            )
          }
        } else {
          const res = await axiosInstant.get(searchBrand(encodeURIComponent(q)))
          rows = (res.data && res.data.rows) || []
          if (!stale) {
            setOptions(
              rows.slice(0, 10).map((row: any) => ({
                id: row.id,
                label: row.name,
                image: row.logo,
              }))
            )
          }
        }
      } catch (err) {
        if (!stale) setOptions([])
      } finally {
        if (!stale) setSearching(false)
      }
    }, 300)
    return () => {
      stale = true
      window.clearTimeout(timer)
    }
  }, [query, isProduct])

  const doImport = async (match?: MatchOption | null) => {
    setImporting(true)
    setInlineError(null)
    const label = stagedName || `#${props.record.id}`
    try {
      const endpoint = isProduct
        ? rmnPromoteDiscoveredProductEndpoint(props.record.id)
        : rmnPromoteDiscoveredBrandEndpoint(props.record.id)
      const body: Record<string, unknown> = {}
      if (match) {
        if (isProduct) body.desire_id = match.id
        else body.brand_id = match.id
      }
      if (isProduct && categoryId) body.product_category_id = categoryId
      const { data } = await axiosInstant.post(endpoint, body)
      // Brand imports auto-start the brand report (import ⇒ report); the
      // Brands page's reports section shows it generating.
      const reportNote =
        !isProduct && data && data.report_id
          ? ' Its brand report is generating on the Brands page.'
          : ''
      toast.success(
        match
          ? `Linked "${label}" to ${match.label}.${reportNote}`
          : `Imported "${label}" — added to your catalog.${reportNote}`
      )
      onImported()
    } catch (err: any) {
      const code = apiErrorCode(err)
      if (code === 'CategoryRequired') {
        setInlineError(`Choose a TDC category to import this ${noun}.`)
      } else if (code === 'AlreadyPromoted') {
        toast.success('Already imported — refreshing the list.')
        onImported()
      } else if (err && err.response && err.response.status === 429) {
        toast.error('Import limit reached — please try again in a bit.')
      } else {
        toast.error(`Couldn't import "${label}". Please try again.`)
      }
    } finally {
      setImporting(false)
    }
  }

  const createDisabled = importing || (isProduct && !categoryId)

  return (
    <Dialog open fullWidth maxWidth="md" onClose={() => !importing && onClose()}>
      <DialogTitle>
        <div className="flex justify-between items-center">
          <Typography variant="h5" className="text-[20px]">
            Import {noun}
          </Typography>
          <IconButton onClick={onClose} disabled={importing} aria-label="Close">
            <CloseIcon className="text-text-2" />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <Box className="flex flex-col md:flex-row gap-6">
          {/* Crawled record + required category */}
          <Box className="flex-1 min-w-0">
            <Typography variant="subtitle2" gutterBottom>
              Discovered {noun}
            </Typography>
            <Box className="flex gap-3 p-3 rounded-lg border border-border-token bg-surface-2">
              {stagedImage && (
                <Box
                  component="img"
                  src={stagedImage}
                  alt=""
                  sx={{
                    width: 64,
                    height: 64,
                    objectFit: 'contain',
                    borderRadius: 1,
                    bgcolor: 'var(--surface)',
                    flexShrink: 0,
                  }}
                />
              )}
              <Stack spacing={0.5} className="min-w-0">
                <Typography sx={{ fontWeight: 600 }} noWrap>
                  {stagedName}
                </Typography>
                {stagedUrl && (
                  <Link href={stagedUrl} target="_blank" rel="noreferrer" variant="caption" noWrap>
                    {stagedUrl}
                  </Link>
                )}
                {props.mode === 'product' && props.record.price && (
                  <Typography variant="body2">{props.record.price}</Typography>
                )}
                {props.mode === 'product' && props.record.rmn_category && (
                  <Typography variant="caption" color="text.secondary">
                    Found in: {props.record.rmn_category.name}
                  </Typography>
                )}
              </Stack>
            </Box>

            {supplierLabel && (
              <Typography variant="body2" sx={{ mt: 1.5 }}>
                Will be linked to: <strong>{supplierLabel}</strong>
              </Typography>
            )}

            {isProduct && (
              <Box sx={{ mt: 2 }}>
                <label
                  htmlFor="import-product-category"
                  className="block text-[13px] font-semibold text-text-1 mb-1.5"
                >
                  TDC category <span className="text-down">*</span>
                </label>
                <select
                  id="import-product-category"
                  value={categoryId}
                  disabled={importing}
                  onChange={(e) => {
                    setInlineError(null)
                    setCategoryId(e.target.value ? Number(e.target.value) : '')
                  }}
                  className="w-full px-3 py-2.5 text-[14px] border border-border-token rounded-lg bg-surface cursor-pointer"
                >
                  <option value="">Select a category…</option>
                  {groupedCategoryOptions(categories)}
                </select>
                <div className="text-[12px] text-muted mt-1.5">
                  Products need a TDC category to be imported.
                </div>
              </Box>
            )}

            {inlineError && (
              <Typography variant="body2" sx={{ color: 'var(--down)', mt: 1.5 }}>
                {inlineError}
              </Typography>
            )}
          </Box>

          <Divider orientation="vertical" flexItem className="hidden md:block" />

          {/* Network catalog search */}
          <Box className="flex-1 min-w-0">
            <Typography variant="subtitle2" gutterBottom>
              Link to existing {noun}
            </Typography>
            <TextField
              fullWidth
              autoFocus
              size="small"
              placeholder={isProduct ? 'Search your products' : 'Search brands'}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                endAdornment: searching ? <CircularProgress size={16} /> : undefined,
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              Already in your catalog? Link it — linking never creates a duplicate.
            </Typography>
            <Stack spacing={1} sx={{ mt: 1.5, maxHeight: 280, overflowY: 'auto' }}>
              {/* The record's existing match, pinned so it stays pickable
                  no matter what the search shows. */}
              {seededMatch && !options.some((o) => o.id === seededMatch.id) && (
                <MatchRow
                  opt={seededMatch}
                  caption="Current match"
                  selected={!!selected && selected.id === seededMatch.id}
                  disabled={importing}
                  onToggle={() =>
                    setSelected((cur) => (cur && cur.id === seededMatch.id ? null : seededMatch))
                  }
                />
              )}
              {options.map((opt) => (
                <MatchRow
                  key={opt.id}
                  opt={opt}
                  selected={!!selected && selected.id === opt.id}
                  disabled={importing}
                  onToggle={() =>
                    setSelected((cur) => (cur && cur.id === opt.id ? null : opt))
                  }
                />
              ))}
              {!searching && query.trim() && options.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  No matches — import as a new {noun} instead.
                </Typography>
              )}
            </Stack>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions className="!px-6 !py-4">
        {selected && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
            Linking connects this discovered {noun} to the existing record — nothing is duplicated.
          </Typography>
        )}
        <Button variant="outlined" disabled={importing} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={selected ? importing : createDisabled}
          onClick={() => doImport(selected)}
        >
          {selected ? `Link to ${selected.label}` : `Import as new ${noun}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// One selectable search result / current-match row. Clicking toggles the
// selection; the footer CTA performs the actual link.
function MatchRow({
  opt,
  caption,
  selected,
  disabled,
  onToggle,
}: {
  opt: MatchOption
  caption?: string
  selected: boolean
  disabled: boolean
  onToggle: () => void
}) {
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => !disabled && onToggle()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) onToggle()
      }}
      className="flex items-center justify-between gap-2 p-2 rounded-lg cursor-pointer"
      sx={{
        border: selected ? '1.5px solid var(--accent-text)' : '1px solid var(--border)',
        background: selected ? 'var(--tint)' : 'var(--surface)',
      }}
    >
      <Box className="flex items-center gap-2 min-w-0">
        {opt.image && (
          <Box
            component="img"
            src={opt.image}
            alt=""
            sx={{
              width: 32,
              height: 32,
              objectFit: 'contain',
              borderRadius: 0.5,
              flexShrink: 0,
            }}
          />
        )}
        <Box className="min-w-0">
          {caption && (
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'var(--accent-text)', fontWeight: 700, lineHeight: 1.2 }}
            >
              {caption}
            </Typography>
          )}
          <Typography variant="body2" noWrap>
            {opt.label}
          </Typography>
        </Box>
      </Box>
      <Button size="small" variant={selected ? 'contained' : 'outlined'} disabled={disabled}>
        {selected ? 'Selected' : 'Select'}
      </Button>
    </Box>
  )
}
